import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Button, Icon, IconButton, ListItemIcon, Menu, MenuItem, Snackbar, TablePagination, Tooltip } from '@mui/material';

import ProgressBar from '../../controls/ProgressBar';
import ErrorMessage from '../../controls/ErrorMessage';
import { ListParams } from '../../../models/listParams';
import { portfolioService } from '../../../services/portfolio.service';
import { Task } from '../../../models/portfolio/task';
import { PagedResult } from '../../../models/pagedResult';
import DialogTaskAdd from '../../controls/dialogs/DialogTaskAdd';
import { contentService } from '../../../services/content.service';
import DialogBase from '../../controls/dialogs/DialogBase';
import DialogTimeline from '../../controls/dialogs/DialogTimeline';
import { helperService } from '../../../services/helper.service';
import AnnotationReplies from './AnnotationReplies';
import { eventBus } from '../../../services/event-emitter.service';


interface OverzichtTasksProps {
  portfolioId?: string;
  status?: string;
  mijnOrganisatieId?: string;
  organizations?: any[];
  refreshKey?: number;
  readOnly?: boolean;
}

interface TaskResponse extends PagedResult<Task> {}

const OverzichtTasks: React.FC<OverzichtTasksProps> = ({ 
  portfolioId,
  status,
  mijnOrganisatieId,
  organizations,
  refreshKey,
  readOnly = false,
}) => {
  const { accounts } = useMsal();
  const account = accounts[0];

  const [totalCount, setTotalCount] = useState(0);
  const [tasks, setTasks] = useState<TaskResponse>({
    count: 0,
    offset: 0,
    limit: 10,
    items: []
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [expandedReplies, setExpandedReplies] = useState<Record<string, boolean>>({});

  const [selectedTask, setSelectedTask] = useState<Task | undefined>(undefined);
  const [selectedTaskHtml, setSelectedTaskHtml] = useState<string>('');
  const [archivedTasks, setArchivedTasks] = useState<Task[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [open, setOpenDialogs] = useState({
    task: false,
    taskShow: false,
    timeline: false,
    replies: false,
    snackbar: false
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  // Handlers to open/close each dialog
  const openDialog = (dialog: keyof typeof open) => {
    // setSelectedId(id);
    setOpenDialogs((prev) => ({ ...prev, [dialog]: true }));
  };

  const closeDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: false }));
  };

  // Handlers for menu
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, task: Task) => {
    setSelectedTask(task);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // setSelectedTask(undefined); // Niet nodig
  };

  useEffect(() => {
    setLoading(false);
    fetchTasks();

  }, [refreshKey, page, pageSize]); // Empty dependency array ensures this runs once when the component mounts

  useEffect(() => {
    eventBus.on('reloadTasks', fetchTasks);

    // Cleanup
    return () => {
      eventBus.off('reloadTasks', fetchTasks);
    };
  }, []);

  const fetchTasks = async () => {
    try {
      if (portfolioId) {
        setLoading(true);
  
        const offset = page * pageSize;
  
        const params: ListParams = {
          offset,
          limit: pageSize,
          ...(status && { filter: ` status = '${status}' ` }), // Status filter
        };

        // Filter examples
        // status = 'ongoing', // status in('ongoing', 'pending') // status != 'completed' // status != 'cancelled'
  
        const data = await portfolioService.getTasksByPortfolio(portfolioId, params) as any;
  
        if (data) {
          const sortedItems = [...data.items].sort((a, b) =>
            new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
          );
  
          setTotalCount(data.count);
          setTasks({ ...data, items: sortedItems });
        }
  
        setLoading(false);
      }
    } catch (error: any) {
      setError(error.message || 'Er is een fout opgetreden');
      setLoading(false);
    }
  };

  const fetchTasksArchived = async (taskId: string) => {
    try {
      if (taskId) {
        setLoading(true);
  
        const data = await portfolioService.getTasksArchived(taskId) as any;
        if (data) {
          // console.log('Fetched archived tasks:', data);

          const sortedItems = [...data.items].sort((a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
  
          setArchivedTasks(sortedItems);
        }
  
        setLoading(false);
      }
    }
    catch (error: any) {
      setError(error.message || 'Er is een fout opgetreden');
      setLoading(false);
    }
  };

  // const fetchTaskAnnotations = async (taskId: string, offset = 0, limit = 5) => {
  //   try {
  //     if (taskId) {
  //       setLoading(true);
  
  //       const params: ListParams = { 
  //         offset,
  //         limit 
  //       };
  
  //       const data = await portfolioService.getAnnotationsByTask(taskId, params);
  
  //       if (data) {
  //         // Handle annotations data if needed
  //         console.log('Task annotations data:', data);
  //       }
  
  //       setLoading(false);
  //     }
  //   }
  //   catch (error: any) {
  //     setError(error.message || 'Er is een fout opgetreden');
  //     setLoading(false);
  //   }
  // };

  // const onClickRepliesTask = async (task: Task) => {
  //   setSelectedTask(task);
  //   if (selectedTask) {
  //     await fetchTaskAnnotations(task.id || '');
  //     openDialog('replies');
  //     handleMenuClose();
  //   }
  // };

  const toggleRepliesVisible = async (task: Task) => {
    setExpandedReplies((prev) => ({
      ...prev,
      [task.id]: !prev[task.id],
    }));

    // if (!expandedReplies[task.id] &&
    //   (task.children?.items.length ?? 0) < repliesPageSize &&
    //   (task.children?.count ?? 0) > repliesPageSize) {
    //     await fetchTaskAnnotations(task.id || '');
    // }
  };

  const onClickHistoryTask = async (task: Task) => {
    setSelectedTask(task);
    if (selectedTask) {
      await fetchTasksArchived(task.id || '');
      openDialog('timeline');
      handleMenuClose();
    }
  };

  // const onClickAddTask = async () => {
  //   setSelectedTask(undefined); // empty for new task
  //   openDialog('task');
  //   handleMenuClose();
  // };

  const onClickEditTask = async (task: Task) => {
    if (readOnly) {
      return;
    }

    setSelectedTask(task);
    if (selectedTask) {
      openDialog('task');
      handleMenuClose();
    }
  };

  const onClickShowTask = async () => {
    if (!selectedTask) return;
  
    const task = selectedTask;
  
    const html = `
      <div>
        <strong>ID:</strong> ${task.id ?? '-'}<br/>
        <strong>Portfolio:</strong> ${task.portfolioId ?? '-'}<br/>
        <strong>Titel:</strong> ${task.title ?? '-'}<br/>
        <strong>Omschrijving:</strong> ${task.description ?? '-'}<br/>
        <strong>Status:</strong> ${task.status ? getTaskStatusLabel(task.status) : '-'}<br/>
        <strong>Verantwoordelijk:</strong> ${getOrganisatieNaam(task.assignedOrganization ?? '')}<br/>
        <strong>Deadline:</strong> ${helperService.formatDutchDate(task.dueDate)}<br/>
        <strong>Aangemaakt op:</strong> ${helperService.formatDutchDate(task.timestamp)}<br/>
        <strong>Door gebruiker:</strong> ${task.createdByUser ?? '-'}<br/>
        <strong>Door organisatie:</strong> ${getOrganisatieNaam(task.createdByOrganization ?? '')}<br/>
      </div>
    `;
  
    setSelectedTaskHtml(html);
    openDialog('taskShow');
    handleMenuClose();
  };
  

  const onClickCancelTask = async () => {
    if (readOnly) {
      return;
    }

    handleMenuClose();
    if (selectedTask) {
      const task = selectedTask;
      task.status = 'cancelled';
      handleSubmitTask(task);
      setSnackbarMessage(`De actie ${task.title} is geannuleerd`);
      openDialog('snackbar');
    }
  };

  const handleSubmitTask = async (task: Task) => {
    if (readOnly) {
      return;
    }

    try {
      setLoading(true);

      if (task) {

        let response;
        if (task.id) {
          response = await portfolioService.putTask(task);
        }
        else {
          response = await portfolioService.postTask(task);
        }
        
        if (response) { // response.ok
          // setTimeout(() => {
          //   fetchTasks();
          // }, 2500);

          setTimeout(() => {
            eventBus.emit('reloadTasks');

            setLoading(false);
            setSnackbarMessage('Actie succesvol opgeslagen');
            openDialog('snackbar');
          }, 2000);
        }
      } 
      else {
        console.error('Geen actie om toe te voegen');
      }
    }
    catch (error) {
      setLoading(false);
      console.error('Error updating task:', error);
      setError('Er is een fout opgetreden');
    }
  };

  const getDeadlineClass = (dueDate: string): string => {
    const now = new Date();
    const due = new Date(dueDate);
    const oneWeekFromNow = new Date();
    oneWeekFromNow.setDate(now.getDate() + 7);
  
    if (due < now) return 'verlopen';
    if (due < oneWeekFromNow) return 'binnenkort-verlopen';
    return '';
  };

  const getOrganisatieNaam = (organisatieId: string) => {
    const organisatie = organizations?.find(org => org.id === organisatieId);
    return organisatie?.name ?? organisatieId; // fallback to ID if name not found
  };

  const getTaskStatusIcon = (id: string) => {
    const statusIcon = contentService.getTaskStatusIcon(id);
    return statusIcon;
  };

  const getTaskStatusLabel = (id: string) => {
    const statusLabel = contentService.getTaskStatusLabel(id);
    return statusLabel ?? id; // fallback to id if label not found
  };

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={open.snackbar}
        autoHideDuration={2000}
        onClose={() => closeDialog('snackbar')}
        message={snackbarMessage ? snackbarMessage : 'Er is een fout opgetreden'}
        key={'topcenter'}
      />

      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {/* <MenuItem
          onClick={() => onClickEditTask() }
        >
          <ListItemIcon>
            <Icon>edit</Icon>
          </ListItemIcon>
          Wijzigen
        </MenuItem> */}
        <MenuItem
          onClick={() => onClickShowTask() }
        >
          <ListItemIcon>
            <Icon>list</Icon>
          </ListItemIcon>
          Toon alle gegevens
        </MenuItem>
        {/* <MenuItem
          onClick={() => onClickHistoryTask() }
        >
          <ListItemIcon>
            <Icon>timeline</Icon>
          </ListItemIcon>
          Toon historie
        </MenuItem>
        <MenuItem
          onClick={() => onClickRepliesTask() }
        >
          <ListItemIcon>
            <Icon>chat</Icon>
          </ListItemIcon>
          Bekijk reacties
        </MenuItem> */}
        {!readOnly && (
          <MenuItem
            onClick={() => onClickCancelTask() }
          >
            <ListItemIcon>
              <Icon>cancel</Icon>
            </ListItemIcon>
            Actie annuleren
          </MenuItem>
        )}
      </Menu>

      <div className="tasks">
        {tasks.count === 0 && tasks.items.length === 0 && (
          <div className="task-item">
            <div className="task-content">
              <div className="task-header">
                <p>Er zijn nog geen acties</p>
              </div>
            </div>
          </div>
        )}

        {(tasks.items ?? []).map(task => (

          <div key={task.id} className="task-item">
            {/* Content */}
            <div className="task-content">

              {/* Header: title + status */}
              <div className="task-header">
                <h4 className="task-title">{task.title}</h4>
                <p>{task.description}</p>
              </div>

              <div className="task-footer">
                <div className="meta">
                  {/* <div className="priority">
                    <Tooltip
                      title={`Prioriteit is hoog`}
                      arrow
                    >
                      <div className="task-priority">
                        <Icon className="priority">keyboard_double_arrow_up</Icon>
                        <span>Hoog</span>
                      </div>
                    </Tooltip>
                  </div>
                  <div className="date">{helperService..formatDutchDate(task.timestamp)}</div>
                  <div className="user">{task.createdByUser}</div> */}
                  <Tooltip
                      title={`Verantwoordelijk: ${getOrganisatieNaam(task.assignedOrganization ?? '')}`}
                      arrow
                    >
                    <div className="organization">{getOrganisatieNaam(task.assignedOrganization ?? '')}</div>
                  </Tooltip>

                  {task.dueDate && (
                    <Tooltip
                      title={`Deadline: ${helperService.formatDutchDate(task.dueDate)}`}
                      arrow
                    >
                      <div className={`due-date ${getDeadlineClass(task.dueDate)}`}>
                        <Icon className="icon-cirle">schedule</Icon>
                        <span>{helperService.formatDutchDate(task.dueDate)}</span>
                      </div>
                    </Tooltip>
                  )}

                  <Tooltip
                    title={`Status is ${getTaskStatusLabel(task.status || '')}`}
                    arrow
                    >
                      <div className={`task-status ${task.status}`}>
                        <Icon className={`icon-cirle ${task.status}`}>{getTaskStatusIcon(task.status || '')}</Icon>
                        <span>{getTaskStatusLabel(task.status || '')}</span>
                      </div>
                  </Tooltip>

                  {task.annotationCount && task.annotationCount > 0 ? (
                    <div className="replies">
                      <Button
                        variant="text"
                        color="secondary"
                        className="btn-small"
                        onClick={() => toggleRepliesVisible(task)}
                      >
                        {task.annotationCount === 1
                          ? '1 opmerking'
                          : `${task.annotationCount || 0} opmerkingen`
                        }
                        <Icon>
                          {task.id && expandedReplies[task.id] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                        </Icon>
                      </Button>
                    </div>
                  ) : (
                    <div className="replies">Geen opmerkingen</div>
                  )}

                </div>

                <div className="actions">
                  {/* <Tooltip title="Actie annuleren" arrow>
                    <IconButton
                      color="secondary"
                      size="small"
                      onClick={() => {
                        onClickCancelTask(task);
                      }}
                    >
                      <Icon>cancel</Icon>
                    </IconButton>
                  </Tooltip> */}

                  {!readOnly && (
                    <Tooltip title="Voeg een opmerking toe" arrow>
                      <IconButton
                        color="secondary"
                        size="small"
                        onClick={() => {
                          toggleRepliesVisible(task);  // open replies if closed
                          // onClickAddReply(annotation.id);
                        }}
                      >
                        <Icon>reply</Icon>
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  <Tooltip title="Bekijk historie" arrow>
                    <IconButton
                      color="secondary"
                      size="small"
                      onClick={() => {
                        onClickHistoryTask(task);
                      }}
                    >
                      <Icon>timeline</Icon>
                    </IconButton>
                  </Tooltip>

                  {!readOnly && (
                    <Tooltip title="Wijzigen actie" arrow>
                      <IconButton
                        color="secondary"
                        size="small"
                        onClick={() => {
                          onClickEditTask(task);
                        }}
                      >
                        <Icon>edit</Icon>
                      </IconButton>
                    </Tooltip>
                  )}
                 
                  {/* <Tooltip title="Toon alle gegevens" arrow>
                    <IconButton
                      color="secondary"
                      size="small"
                      onClick={() => {
                        onClickShowTask(task.id);
                      }}
                    >
                      <Icon>list</Icon>
                    </IconButton>
                  </Tooltip> */}

                  <IconButton 
                    className="btn-task-more"
                    onClick={(e) => handleMenuOpen(e, task)}
                  >
                    <Tooltip title="Acties" placement="right" arrow>
                      <Icon>more_vert</Icon>
                    </Tooltip>
                  </IconButton>
                </div>
              </div>
            </div>

            <AnnotationReplies
              portfolioId={portfolioId}
              object={task}
              type='task'
              visible={expandedReplies[task.id] || false}
              account={account}
              organizations={organizations}
              mijnOrganisatieId={mijnOrganisatieId}
              readOnly={readOnly}
            />

          </div>
        ))}

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0); // reset to first page when size changes
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Acties per pagina:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} van ${count}`}
        />
      </div>

      <DialogBase
        open={open.taskShow}
        title="Actie"
        contentText="Overzicht van alle gegevens van de actie."
        onClose={ () => closeDialog('taskShow') }
      >
        <div dangerouslySetInnerHTML={{ __html: selectedTaskHtml }} />
      </DialogBase>

      <DialogTaskAdd
        open={open.task}
        portfolioId={portfolioId}
        organizations={organizations}
        selectedTask={selectedTask}
        onClose={ () => closeDialog('task') }
        onSubmit={handleSubmitTask}
      />

      {archivedTasks.length > 0 && (
        <DialogTimeline
          open={open.timeline}
          items={archivedTasks as any[]}
          type="task"
          title={`Historie van deze actie: ${selectedTask ? selectedTask.title : ''}`}
          organizations={organizations}
          onClose={ () => closeDialog('timeline') }
        />
      )}

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}
    </>
  );
};

export default OverzichtTasks;
